import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from './Button';

function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const values = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...values].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);
  const result = [];
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) result.push(`gap-${page}`);
    result.push(page);
  });
  return result;
}

export default function Pagination({ page, count, pageSize = 20, onPageChange, className = '' }) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil((count || 0) / pageSize));
  if (!count || totalPages <= 1) return null;

  const goTo = (nextPage) => {
    const bounded = Math.min(totalPages, Math.max(1, nextPage));
    if (bounded !== page) onPageChange(bounded);
  };

  return (
    <nav className={`flex flex-col items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row ${className}`} aria-label={t('Pagination')}>
      <p className="text-xs text-gray-500">
        {t('Showing {{from}}–{{to}} of {{count}}', {
          from: (page - 1) * pageSize + 1,
          to: Math.min(page * pageSize, count),
          count,
        })}
      </p>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => goTo(page - 1)}>{t('Previous')}</Button>
        <div className="hidden items-center gap-1 sm:flex">
          {pageNumbers(page, totalPages).map((value) => typeof value === 'string' ? (
            <span key={value} className="px-1 text-gray-400">…</span>
          ) : (
            <button
              key={value}
              type="button"
              onClick={() => goTo(value)}
              aria-current={value === page ? 'page' : undefined}
              className={`h-8 min-w-8 rounded-lg px-2 text-sm font-medium transition ${value === page ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {value}
            </button>
          ))}
        </div>
        <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => goTo(page + 1)}>{t('Next')}</Button>
      </div>
    </nav>
  );
}

